import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { Suspense, useState, useEffect, useCallback } from "react";
import { IVehicleB2BDetails } from "@/types/contractB2B.type";
import { useParams } from "react-router-dom";
import { getStatusLabelsVehicle } from "@/types/vehicle.type";
import { getB2BVehiclesConsolidated } from "@/common/services/contracts-b2b.api";
import { getCompanySigla } from "@/common/helpers/company.helpers";

const headers = {
  companyId: "Cidade/Locadora",
  licensePlate: "Placa",
  model: "Modelo",
  renterName: "Locatário",
  status: "Status Veículo",
};

type TableRow = Record<string, unknown>;

export default function VehiclesTableB2B() {
  const { id: idCompanyB2B } = useParams<{ id: string }>();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 999, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [vehicles, setVehicles] = useState<IVehicleB2BDetails[]>([]);

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "licensePlate,model,renterName",
    orderBy: "licensePlate",
    itemsPerPage: "999",
  });

  const loadVehicleData = useCallback(async () => {
    if (!idCompanyB2B) return;

    setIsLoading(true);
    setVehicles([]);
    try {
      const result = await getB2BVehiclesConsolidated(idCompanyB2B);

      if (!result.ok) {
        throw new Error("Erro ao buscar a frota associada.");
      }

      const loadedVehicles = result.data || [];
      setVehicles(loadedVehicles);
      setData((prev) => ({ ...prev, totalItems: loadedVehicles.length }));
    } catch (error) {
      console.error(error);
      notifyError(
        (error as Error).message || "Falha ao carregar veículos associados."
      );
      setVehicles([]);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCompanyB2B]);

  useEffect(() => {
    loadVehicleData();
  }, [loadVehicleData]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const actions = [];

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section p-4">
        <TableFilters
          dashboardHeader={`Frota B2B Associada (${data.totalItems})`}
          placeholder="Buscar Placa, Modelo ou Locatário."
          statusOptions={{}}
          entity="vehicle"
          onSearch={handleFilterChange}
          tableData={vehicles as unknown as TableRow[]}
        />

        <GenericTable
          headers={headers}
          data={vehicles as unknown as TableRow[]}
          actions={actions}
          setFilters={({ orderBy }) => {
            handleFilterChange({ orderBy });
          }}
          renders={{
            companyId: (companyId) => {
              return getCompanySigla(companyId as string) || "";
            },
            status: (statusData) => {
              const status = statusData as string;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              return getStatusLabelsVehicle(status as any);
            },
            renterName: (name) => {
              const renter = name as string;
              return renter || "N/A";
            },
            licensePlate: (plate) => (
              <span className="font-semibold">{plate as string}</span>
            ),
          }}
          entity="vehicle"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 999)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}
